import classNames from 'classnames'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  getProductPriceBadge,
  isShowEverydayPrice,
  ProductPriceDiscountType,
  splitFormattedPrice,
} from '~/helpers'
import { useFormatPrice } from '~/hooks'
import styles from './ProductDashboardPrice.module.scss'
import { ProductDashboardPriceProps } from './ProductDashboardPrice.types'

const BADGE_CLASS_NAMES: Record<ProductPriceDiscountType, string> = {
  [ProductPriceDiscountType.Promo]: styles.discountBadgePromo,
  [ProductPriceDiscountType.Member]: styles.discountBadgeMember,
  [ProductPriceDiscountType.Trade]: styles.discountBadgeTrade,
  [ProductPriceDiscountType.Staff]: styles.discountBadgeStaff,
}

export const ProductDashboardPrice = ({
  regularPrice,
  finalPrice,
  hasPromoDiscount = false,
  hasCustomerTypeDiscount = false,
  resolvedCustomerType,
}: ProductDashboardPriceProps) => {
  const { formatPrice } = useFormatPrice()

  const {
    symbol: finalPriceSymbol,
    dollars: finalPriceDollars,
    cents: finalPriceCents,
  } = splitFormattedPrice(formatPrice(finalPrice))

  const {
    symbol: regularPriceSymbol,
    dollars: regularPriceDollars,
    cents: regularPriceCents,
  } = splitFormattedPrice(formatPrice(regularPrice))

  const badge = getProductPriceBadge({
    hasPromoDiscount,
    hasCustomerTypeDiscount,
    resolvedCustomerType,
  })
  const isShowEverydayPriceRow = isShowEverydayPrice({
    finalPrice,
    regularPrice,
    hasPromoDiscount,
    resolvedCustomerType,
  })

  return (
    <div className={styles.container}>
      <div className={styles.priceRow}>
        {finalPrice && (
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyLargeBold}
            className={styles.finalPrice}>
            <span className={styles.finalPriceSymbol}>{finalPriceSymbol}</span>
            {finalPriceDollars}
            {finalPriceCents && (
              <span className={styles.finalPriceCents}>.{finalPriceCents}</span>
            )}
          </Typography>
        )}

        {badge.label && (
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}
            className={classNames(
              styles.discountBadge,
              badge.type && BADGE_CLASS_NAMES[badge.type]
            )}>
            {badge.label}
          </Typography>
        )}
      </div>

      {isShowEverydayPriceRow && (
        <div className={styles.everydayPriceRow}>
          <Typography
            tag={TypographyTag.del}
            variant={TypographyVariant.BodyRegular}>
            {regularPriceSymbol}
            {regularPriceDollars}
            {regularPriceCents && `.${regularPriceCents}`}
          </Typography>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}>
            Everyday Price
          </Typography>
        </div>
      )}
    </div>
  )
}
